<template>
  <div>
    <div class="d-flex align-center">
      <h2>Newsletter List</h2>
      <v-spacer />
      <v-btn @click="editDialog = true" color="primary" variant="flat">
        <v-icon start>mdi-plus</v-icon>
        Create New
      </v-btn>
    </div>

    <!-- table -->
    <v-data-table
      class="mt-6"
      :items="lettersResponse ? lettersResponse.items : []"
      :headers="headers"
      :loading="loading"
      hide-default-footer
      :items-per-page="-1"
      color="primary"
    >
      <!-- edit -->
      <template v-slot:[`item.category`]="{ item }">
        {{
          item.design.category == "healthLetter" ? "Health Newsletter" : "School Newsletter"
        }}
      </template>

      <!-- edit -->
      <template v-slot:[`item.edit`]="{ item }">
        <v-btn :to="`/letter/${item.letterId}`" color="primary" small variant="flat">
          Edit
        </v-btn>
      </template>

      <!-- duplicate -->
      <template v-slot:[`item.duplicate`]="{ item }">
        <v-btn
          @click="duplicateLetter(item.letterId)"
          color="primary"
          small
          variant="outlined"
        >
          Duplicate
        </v-btn>
      </template>
      <!-- duplicate -->
      <template v-slot:[`item.delete`]="{ item }">
        <v-btn
          @click="deleteLetter(item.letterId)"
          style="background-color: #ff5252; color: #ffffff; font-weight: bold;"
          small
          variant="outlined"
        >
          Delete
        </v-btn>
      </template>
    </v-data-table>

    <!-- pagination -->
    <v-pagination
      class="mt-6"
      v-if="lettersResponse"
      v-model="page"
      @input = "pageClick"
      :length="Math.ceil(lettersResponse.total / searchFilters.limit)"
    ></v-pagination>

    <!-- dialog -->
    <v-dialog v-model="editDialog" max-width="800px">
      <v-card>
        <v-card-title>Newsletter</v-card-title>
        <v-card-text>
          <v-container>
            <v-row align="center">
              <v-col cols="6"> Color </v-col>
              <v-col cols="6">
                <v-select
                  v-model="templateInput.isFullColor"
                  :items="fullcolorItems"
                  density="compact"
                  hide-details
                  variant="outlined"
                  :disabled="loading"
                ></v-select>
              </v-col>
            </v-row>
            <v-row align="center">
              <v-col cols="6"> Month </v-col>
              <v-col cols="6">
                <v-select
                  v-model="templateInput.month"
                  :items="monthItems"
                  density="compact"
                  hide-details
                  variant="outlined"
                  suffix=""
                  :disabled="loading"
                ></v-select>
              </v-col>
            </v-row>
            <v-row v-if="templateResponse" class="mt-4">
              <v-col
                cols="3"
                v-for="(item, i) in templateResponse.designs"
                :key="i"
              >
                <router-link
                  :to="`/letter?designSlug=${item.designSlug}&month=${templateInput.month}&isFullColor=${templateInput.isFullColor}&category=${item.category}&url=${item.url}`"
                >
                  <v-img :src="item.url" contain></v-img>
                </router-link>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ApiLetter from "@/api/ApiLetter";
import LocalDataService from "@/service/LocalDataService";
import { LetterListResponse, LetterTemplateResponse } from "chaild-api/lib";
import { monthItems } from "@/constants/letter";

export default defineComponent({
  name: "LetterList",

  async mounted() {
    const letterListPage = sessionStorage.getItem('letterListPage');
    if (letterListPage) this.page = Number(letterListPage);
    await this.listLetters();
  },

  watch: {
    templateInput: {
      deep: true,
      async handler(val) {
        if (val.month != "") {
          await this.listTemplates();
        }
      },
    },
  },

  data() {
    return {
      loading: false,
      lettersResponse: null as null | LetterListResponse,
      templateResponse: null as null | LetterTemplateResponse,
      editDialog: false,
      headers: [
        { title: "Registration Date", value: "date" },
        { title: "Created By", value: "createdBy" },
        { title: "Newsletter Name", value: "name" },
        { title: "Newsletter Type", value: "category" },
        { title: "Edit", value: "edit" },
        { title: "Duplicate", value: "duplicate" },
        { title: "Delete", value: "delete" },
      ],
      fullcolorItems: [
        { title: "Color", value: true },
        { title: "Monochrome", value: false },
      ],
      monthItems: monthItems,
      templateInput: {
        isFullColor: true,
        month: "",
      },
      searchFilters: {
        skip: 0,
        limit: 20,
      },
    };
  },

  computed: {
    page: {
      get(): number {
        if (this.searchFilters.skip == 0) {
          return 1;
        }
        return Math.ceil(
          this.searchFilters.skip / (this.searchFilters.limit - 1)
        );
      },
      set(value: number) {
        this.searchFilters.skip = (value - 1) * this.searchFilters.limit;
        this.listLetters();
      },
    },
  },

  methods: {
    async pageClick(pageNumber) {
      if (pageNumber) sessionStorage.setItem('letterListPage', String(pageNumber));
    },
    async listLetters() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        this.loading = true;
        const response = await ApiLetter.listLetters({
          nurseryId,
          ...this.searchFilters,
        }).catch(() => (this.loading = false));
        if (response) {
          this.lettersResponse = response;
        }
      }
      this.loading = false;
    },
    async duplicateLetter(letterId: string) {
      const answer = confirm("Do you want to duplicate this newsletter?");
      if (answer && letterId) {
        this.loading = true;
        const response = await ApiLetter.duplicateLetter({
          letterId,
        }).catch(() => (this.loading = false));
        if (response && response.letterId) {
          this.$router.push({ path: `/letter/${response.letterId}` });
        }
      }
      this.loading = false;
    },
    async deleteLetter(letterId: string) {
      const answer = confirm("Are you sure you want to delete?");
      if (answer && letterId) {
        this.loading = true;
        const response = await ApiLetter.deleteLetter({
            letterId: letterId,
          }).catch(() => (this.loading = false));
          if (response) {
            await this.listLetters();
          }
        this.loading = false;
      }
    },
    async listTemplates() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId && this.templateInput.month != "") {
        this.loading = true;
        const response = await ApiLetter.listTemplates({
          nurseryId,
          ...this.templateInput,
        }).catch(() => (this.loading = false));
        if (response) {
          this.templateResponse = response;
        }
      }
      this.loading = false;
    },
  },
});
</script>
